'use client'

import { memo, useEffect, useMemo, useState } from 'react'
import { emoji3dName, emoji3dSrc, jumboCount, splitEmoji } from '@/lib/emoji3d'

/** One 3D emoji. If the picture is missing / fails to load, the normal phone emoji is shown instead (never a broken image). */
export const Emoji3D = memo(function Emoji3D({ char, size, className = '', pop = false }: { char: string; size?: string | number; className?: string; pop?: boolean }) {
  const [failed, setFailed] = useState(false)
  const name = useMemo(() => emoji3dName(char), [char])
  if (!name || failed) return <span className={className} style={size ? { fontSize: size } : undefined}>{char}</span>
  return (
    <img
      className={`e3d ${pop ? 'e3d-pop' : ''} ${className}`}
      src={emoji3dSrc(name)}
      alt={char}
      draggable={false}
      loading="lazy"
      decoding="async"
      onError={() => setFailed(true)}
      style={size ? { width: size, height: size } : undefined}
    />
  )
})

// Animated emoji (Google Noto Animated Emoji, CC BY 4.0 - credit is in the app's About / Terms). Google hosts them; if one does not
// exist (not every emoji is animated) or the network fails, the still 3D picture simply stays - nothing breaks.
const NOTO = 'https://fonts.gstatic.com/s/e/notoemoji/latest'
const notoCodes = (g: string): string[] => {
  const cps = Array.from(g).map((c) => c.codePointAt(0)!.toString(16))
  const full = cps.join('_')
  const noFe = cps.filter((c) => c !== 'fe0f').join('_')
  return full === noFe ? [full] : [full, noFe]
}
const reducedMotion = () => typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

/** Big emoji that moves (Telegram style). The still 3D picture shows instantly, the animation fades in over it once it has loaded. */
export const AnimatedEmoji = memo(function AnimatedEmoji({ char }: { char: string }) {
  const codes = useMemo(() => notoCodes(char), [char])
  const [i, setI] = useState(0)
  const [ready, setReady] = useState(false)
  const [motion, setMotion] = useState(false)
  useEffect(() => { setMotion(!reducedMotion()) }, [])
  const dead = !motion || i >= codes.length
  return (
    <span className="e3d-anim">
      <Emoji3D char={char} pop className={ready ? 'e3d-under' : ''} />
      {!dead && (
        <img
          key={codes[i]}
          className={`e3d-live ${ready ? 'is-ready' : ''}`}
          src={`${NOTO}/${codes[i]}/512.webp`}
          alt=""
          aria-hidden="true"
          draggable={false}
          decoding="async"
          onLoad={() => setReady(true)}
          onError={() => { setReady(false); setI((n) => n + 1) }}
        />
      )}
    </span>
  )
})

/** Text with every emoji drawn as a 3D picture. A message that is only 1-3 emoji is shown big (Telegram style). */
const MENTION_SPLIT = /(@[a-zA-Z0-9_.]{2,30})/g
const renderMentions = (v: string, onMention?: (h: string) => void) => {
  if (!onMention || !v.includes('@')) return v
  return v.split(MENTION_SPLIT).map((seg, i) => /^@[a-zA-Z0-9_.]{2,30}$/.test(seg)
    ? <span key={i} className="mention-link" role="link" onClick={(e) => { e.stopPropagation(); onMention(seg.slice(1)) }}>{seg}</span>
    : seg)
}

export const RichEmojiText = memo(function RichEmojiText({ text, onMention }: { text: string; onMention?: (handle: string) => void }) {
  const parts = useMemo(() => splitEmoji(text), [text])
  if (parts.length === 1 && parts[0].t === 'text') return <>{renderMentions(parts[0].v, onMention)}</>
  const jumbo = jumboCount(parts)
  if (jumbo) {
    return (
      <span className={`e3d-jumbo e3d-jumbo-${jumbo}`}>
        {parts.map((p, i) => (p.t === 'emoji' ? <AnimatedEmoji key={i} char={p.v} /> : null))}
      </span>
    )
  }
  return <>{parts.map((p, i) => (p.t === 'emoji' ? <Emoji3D key={i} char={p.v} /> : <span key={i}>{renderMentions(p.v, onMention)}</span>))}</>
})

/** One emoji at a fixed size, animated (Telegram style) - used in the game lobbies, the in-game chat and the floating emoji over players.
 *  `still` = the 3D picture only (no animation download). */
export const GameEmoji = memo(function GameEmoji({ char, size = 28, still = false }: { char: string; size?: number; still?: boolean }) {
  return (
    <span className="e3d-fix" style={{ width: size, height: size }}>
      {still ? <Emoji3D char={char} size="100%" /> : <AnimatedEmoji char={char} />}
    </span>
  )
})
